import { useEffect, useRef } from "react";
import { Calendar, ChevronRight, Minimize2, Phone, User, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCallSession } from "@/hooks/useCallSession";

const PRE_EMI_REASONS = [
  { value: "जमा कर देंगे", label: "जमा कर देंगे" },
  { value: "जमा नहीं कर पाएंगे", label: "जमा नहीं कर पाएंगे" },
  { value: "जमा कर दिए हैं", label: "जमा कर दिए हैं" },
  { value: "Customer not reachable", label: "Customer not reachable" },
];

const POST_EMI_REASONS = [
  { value: "PTP", label: "PTP – Promise to Pay" },
  { value: "RTP", label: "RTP – Refuse to Pay" },
  { value: "CIH", label: "CIH – Cash in Hand" },
  { value: "Paid", label: "Paid" },
  { value: "RNR", label: "RNR – Ringing No Response" },
  { value: "NC", label: "NC – Non-Contactable" },
  { value: "SW", label: "SW – Switch Off" },
  { value: "CBL", label: "CBL – Call Back Later" },
];

const GlobalCallOverlay = () => {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);
  const {
    phase,
    minimized,
    loading,
    displayName,
    summary,
    commentText,
    selectedReason,
    callingPtpDate,
    emiMode,
    isEmiModeLocked,
    setCommentText,
    setSelectedReason,
    setCallingPtpDate,
    setEmiMode,
    setMinimized,
    saveSummary,
    endSession,
  } = useCallSession();

  const hasActiveSession = phase !== "idle";
  const isSummaryPhase = phase === "summary";
  const isPtpReason = selectedReason === "PTP";
  const minPtpDate = new Date().toISOString().split("T")[0];
  const reasonOptions = emiMode === "pre" ? PRE_EMI_REASONS : POST_EMI_REASONS;

  const handleReasonChange = (value: string) => {
    setSelectedReason(value);
    if (value !== "PTP" && callingPtpDate) {
      setCallingPtpDate("");
    }
  };

  useEffect(() => {
    if (!hasActiveSession) return;
    if (prevPathRef.current !== location.pathname) {
      // On back/navigation, keep call running but show small floating box.
      setMinimized(true);
      prevPathRef.current = location.pathname;
    }
  }, [hasActiveSession, location.pathname, setMinimized]);

  if (!hasActiveSession) return null;

  return (
    <>
      {!minimized && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-white">
          <button
            type="button"
            className="absolute top-4 right-4 rounded-md border border-gray-300 px-3 py-2 text-xs hover:bg-gray-50"
            onClick={() => setMinimized(true)}
          >
            <span className="inline-flex items-center gap-2">
              <Minimize2 className="h-4 w-4" />
              Minimize
            </span>
          </button>

          {!isSummaryPhase || !summary ? (
            <div className="flex flex-col items-center gap-6">
              <div className="flex items-center gap-6">
                <div className="h-12 w-12 rounded-full bg-indigo-100 overflow-hidden flex items-center justify-center">
                  <img
                    src="/uploads/Avatar2.svg"
                    alt="Agent"
                    className="h-10 w-10 rounded-full object-cover"
                  />
                </div>
                <div className="flex items-center gap-1 text-green-500">
                  <ChevronRight className="w-4 h-4" />
                  <ChevronRight className="w-4 h-4" />
                  <ChevronRight className="w-4 h-4" />
                </div>
                <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center">
                  <User className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div className="text-center space-y-1">
                <p className="text-xs text-gray-500">Forwarding your call to</p>
                <p className="text-sm font-semibold text-gray-900">{displayName}</p>
              </div>
            </div>
          ) : (
            <div className="w-[90%] max-w-md space-y-6 overflow-y-auto max-h-[90vh]">
              <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="h-12 w-12 rounded-full bg-purple-100 overflow-hidden flex items-center justify-center flex-shrink-0">
                    <img
                      src="/uploads/Avatar2.svg"
                      alt="Applicant"
                      className="h-full w-full object-cover"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-lg text-gray-900 truncate">
                      {displayName || summary.applicant_id}
                    </p>
                    <div className="flex items-center gap-1 mt-1">
                      <Calendar className="h-3 w-3 text-gray-500" />
                      <p className="text-xs text-gray-600">
                        {summary.call_start_time
                          ? new Date(summary.call_start_time).toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })
                          : new Date().toLocaleDateString("en-GB", {
                              day: "numeric",
                              month: "short",
                              year: "numeric",
                            })}
                      </p>
                    </div>
                  </div>
                  <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700 flex items-center gap-1 flex-shrink-0">
                    <User className="h-3 w-3" />
                    Applicant
                  </span>
                </div>

                <div className="grid grid-cols-3 gap-4 pt-3 border-t border-gray-200">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Start Time</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {summary.call_start_time
                        ? new Date(summary.call_start_time).toLocaleTimeString("en-US", {
                            hour: "numeric",
                            minute: "2-digit",
                            hour12: true,
                          })
                        : "--"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Duration</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {summary.duration_seconds
                        ? `${Math.floor(summary.duration_seconds / 60)}:${String(
                            summary.duration_seconds % 60
                          ).padStart(2, "0")} Mins`
                        : "--"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Pickup Status</p>
                    <p className="text-sm font-semibold text-gray-900">
                      {summary.status === "customer_answered" || summary.status === "answered"
                        ? "Picked up"
                        : summary.status === "customer_not_picked" || summary.status === "not_picked"
                        ? "Not picked"
                        : summary.status || "--"}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center gap-2 w-full">
                  <button
                    type="button"
                    className={`w-1/2 rounded-lg border px-3 py-1.5 text-sm font-medium transition ${
                      emiMode === "pre"
                        ? "border-black bg-black text-white"
                        : isEmiModeLocked
                        ? "border-gray-300 bg-white text-gray-700 cursor-not-allowed opacity-50"
                        : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                    onClick={() => setEmiMode("pre")}
                    disabled={isEmiModeLocked && emiMode !== "pre"}
                    aria-pressed={emiMode === "pre"}
                    title={
                      isEmiModeLocked
                        ? emiMode === "pre"
                          ? "Pre EMI (from call calling_type)"
                          : "Locked by call calling_type"
                        : "Select Pre EMI"
                    }
                  >
                    Pre EMI
                  </button>
                  <button
                    type="button"
                    className={`w-1/2 rounded-lg border px-3 py-1.5 text-sm font-medium transition ${
                      emiMode === "post"
                        ? "border-black bg-black text-white"
                        : isEmiModeLocked
                        ? "border-gray-300 bg-white text-gray-700 cursor-not-allowed opacity-50"
                        : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                    }`}
                    onClick={() => setEmiMode("post")}
                    disabled={isEmiModeLocked && emiMode !== "post"}
                    aria-pressed={emiMode === "post"}
                    title={
                      isEmiModeLocked
                        ? emiMode === "post"
                          ? "Post EMI (from call calling_type)"
                          : "Locked by call calling_type"
                        : "Select Post EMI"
                    }
                  >
                    Post EMI
                  </button>
                </div>
                <p className="text-sm font-medium text-gray-800">Reason *</p>
                <Select value={selectedReason} onValueChange={handleReasonChange}>
                  <SelectTrigger className="w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-black/80">
                    <SelectValue placeholder="Select a reason" />
                  </SelectTrigger>
                  <SelectContent className="z-[10010]">
                    {reasonOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {isPtpReason && (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-800">Calling PTP Date *</p>
                  <input
                    type="date"
                    value={callingPtpDate}
                    onChange={(e) => setCallingPtpDate(e.target.value)}
                    min={minPtpDate}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/80"
                  />
                </div>
              )}

              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-800">Comment (optional)</p>
                <textarea
                  className="w-full min-h-[140px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/80 resize-none"
                  placeholder="Add call summary comment..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  maxLength={200}
                />
                <div className="flex justify-end">
                  <p className="text-xs text-gray-500">{commentText.length}/200</p>
                </div>
              </div>

              <Button
                className="w-full h-11 rounded-lg bg-black text-white hover:bg-gray-900"
                onClick={saveSummary}
                disabled={loading || !selectedReason || (isPtpReason && !callingPtpDate)}
              >
                Save Changes
              </Button>
            </div>
          )}
        </div>
      )}

      {minimized && (
        <div className="fixed bottom-4 right-4 z-[9999] w-[290px] rounded-xl border bg-white shadow-lg p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="text-xs text-gray-500">
                {isSummaryPhase ? "Call completed" : "Ongoing call"}
              </p>
              <p className="text-sm font-semibold text-gray-900 truncate">{displayName}</p>
              <p className="text-xs text-gray-500 mt-1">
                {isSummaryPhase ? "Tap to open summary" : "Tap to reopen call screen"}
              </p>
            </div>
            <button
              type="button"
              className="rounded-md p-1 hover:bg-gray-100"
              onClick={endSession}
              aria-label="Close call session"
              title="Close"
            >
              <X className="h-4 w-4 text-gray-600" />
            </button>
          </div>

          <button
            type="button"
            className="mt-3 w-full rounded-lg bg-black text-white py-2 text-sm flex items-center justify-center gap-2 hover:bg-gray-900"
            onClick={() => setMinimized(false)}
          >
            <Phone className="h-4 w-4" />
            Open Call
          </button>
        </div>
      )}
    </>
  );
};

export default GlobalCallOverlay;
